import React, { useEffect, useState, useCallback } from 'react';
import {
  MapPin, Target, Loader2, RefreshCcw, AlertTriangle, CheckCircle, Play, Eye,
} from 'lucide-react';
import { Button } from '../ui/button';
import { api, friendlyError } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

/**
 * Admin panel for geocoding coverage + bulk backfill.
 *
 * Sections:
 *   1. Coverage stats card (total / with / without / coverage_pct)
 *   2. Per-source breakdown (precise / approximate / centroid / unknown)
 *   3. Dry-run + Real backfill buttons (only_missing toggle)
 *   4. Sample list of properties without coords
 */
export default function GeocodingPanel() {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.adminGeocodingStats();
      setStats(s);
    } catch (e) {
      toast({ title: 'No pudimos cargar', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);

  const onRun = async (dryRun) => {
    if (!dryRun) {
      const target = onlyMissing ? stats?.without_coords : stats?.total;
      const ok = window.confirm(`¿Re-geocodificar ${target} propiedad(es)?\nEsto consultará Nominatim y actualizará lat/lng.`);
      if (!ok) return;
    }
    setRunning(true);
    setLastRun(null);
    try {
      const res = await api.adminGeocodeBackfill(dryRun, 200, onlyMissing);
      setLastRun(res);
      toast({
        title: dryRun ? 'Dry-run completado' : 'Backfill completado',
        description: `Resueltas ${res.updated} · Fallidas ${res.failed_count} · ${res.duration_ms}ms`,
      });
      if (!dryRun) await reload();
    } catch (e) {
      toast({ title: 'Falló', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center border border-gray-200" data-testid="geocoding-panel-loading">
        <Loader2 className="w-8 h-8 text-habita animate-spin mx-auto" />
        <p className="text-sm text-gray-500 mt-3">Cargando cobertura de geocodificación...</p>
      </div>
    );
  }
  if (!stats) return null;

  const coverageColor =
    stats.coverage_pct >= 95 ? 'text-emerald-600' :
    stats.coverage_pct >= 75 ? 'text-amber-600' : 'text-rose-600';

  return (
    <div className="space-y-6" data-testid="geocoding-panel">
      {/* Top banner */}
      <div className={`rounded-2xl border p-5 ${stats.without_coords === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center gap-3">
          {stats.without_coords === 0
            ? <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-habita-dark text-sm">
              {stats.without_coords === 0
                ? 'Cobertura completa — todas las propiedades tienen coordenadas'
                : `${stats.without_coords} propiedad(es) sin coordenadas`}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              Cobertura actual: <strong className={coverageColor}>{stats.coverage_pct}%</strong>
              {' · '}DIVIPOLA (1.122 municipios) usado como centroide de respaldo.
            </p>
          </div>
          <button onClick={reload} data-testid="geocoding-refresh-btn" className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-300 hover:bg-white flex items-center gap-1">
            <RefreshCcw className="w-3 h-3" /> Refrescar
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
          <Target className="w-4 h-4 text-habita" /> Cobertura
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Kpi label="Total propiedades" value={stats.total} testid="geo-kpi-total" />
          <Kpi label="Con coordenadas" value={stats.with_coords} accent="text-emerald-600" testid="geo-kpi-with" />
          <Kpi label="Sin coordenadas" value={stats.without_coords} accent={stats.without_coords ? 'text-amber-600' : 'text-gray-400'} testid="geo-kpi-without" />
          <Kpi label="Cobertura" value={`${stats.coverage_pct}%`} accent={coverageColor} testid="geo-kpi-coverage" />
        </div>

        {/* Per-source breakdown */}
        <div className="mt-5">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Por fuente</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <SourcePill label="Precisa" value={stats.by_source.precise} color="emerald" testid="geo-src-precise" />
            <SourcePill label="Aproximada (barrio)" value={stats.by_source.approximate} color="amber" testid="geo-src-approximate" />
            <SourcePill label="Centroide municipal" value={stats.by_source.centroid} color="sky" testid="geo-src-centroid" />
            <SourcePill label="Sin fuente registrada" value={stats.by_source.unknown_source} color="gray" testid="geo-src-unknown" />
          </div>
        </div>
      </div>

      {/* Backfill controls */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
              <Play className="w-4 h-4 text-habita" /> Ejecutar geocodificación masiva
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Recorre la BD, consulta Nominatim con cache, y aplica fallback al centroide DIVIPOLA cuando es necesario.
            </p>
          </div>
        </div>

        <label className="mt-4 flex items-center gap-2 cursor-pointer w-fit">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
            className="accent-[#00824D]"
            data-testid="geo-only-missing-toggle"
          />
          <span className="text-sm text-gray-700">
            Sólo propiedades sin coordenadas <span className="text-gray-400">(desmarca para re-geocodificar todas)</span>
          </span>
        </label>

        <div className="flex flex-wrap gap-3 mt-4">
          <Button
            onClick={() => onRun(true)}
            disabled={running}
            variant="outline"
            data-testid="geo-dryrun-btn"
            className="h-11 px-5 rounded-full"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Eye className="w-4 h-4 mr-1.5" /> Dry-run</>}
          </Button>
          <Button
            onClick={() => onRun(false)}
            disabled={running || (onlyMissing && stats.without_coords === 0)}
            data-testid="geo-run-btn"
            className="btn-habita h-11 px-5 rounded-full"
          >
            {running
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Geocodificando...</>
              : <><MapPin className="w-4 h-4 mr-1.5" /> Ejecutar backfill</>}
          </Button>
        </div>

        {lastRun && (
          <div className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="geo-last-run">
            <p className="text-sm font-semibold text-habita-dark mb-2 flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              {lastRun.dry_run ? 'Dry-run' : 'Backfill'} · {lastRun.duration_ms}ms
            </p>
            <div className="grid sm:grid-cols-3 gap-2 text-xs">
              <div className="bg-white rounded-lg p-2"><strong className="text-emerald-700">{lastRun.by_source?.precise || 0}</strong> precisas</div>
              <div className="bg-white rounded-lg p-2"><strong className="text-amber-700">{lastRun.by_source?.approximate || 0}</strong> aproximadas</div>
              <div className="bg-white rounded-lg p-2"><strong className="text-sky-700">{lastRun.by_source?.centroid || 0}</strong> centroides</div>
            </div>
            {lastRun.failed_count > 0 && (
              <details className="mt-3">
                <summary className="text-xs font-semibold text-rose-700 cursor-pointer">Ver fallidas ({lastRun.failed_count})</summary>
                <div className="mt-2 max-h-48 overflow-y-auto text-xs text-gray-700 space-y-1 font-mono">
                  {lastRun.failed.map((f, i) => (
                    <div key={i}>· {f.id?.slice(0, 8)} — {f.title || ''} — {f.reason}</div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Sample list of properties without coords */}
      {stats.sample_without_coords?.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200">
          <h3 className="font-serif text-lg font-bold text-habita-dark flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Propiedades sin coordenadas (primeras 5)
          </h3>
          <div className="mt-3 space-y-2">
            {stats.sample_without_coords.map(p => (
              <div key={p.id} className="text-xs text-gray-700 bg-habita-cream rounded-lg p-3 font-mono" data-testid={`geo-sample-${p.id}`}>
                <div className="font-bold text-habita-dark">{p.title}</div>
                <div className="text-gray-500 mt-0.5">
                  {p.address} · {p.neighborhood ? `${p.neighborhood}, ` : ''}{p.city}{p.department ? `, ${p.department}` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="bg-habita-cream rounded-xl p-3" data-testid={testid}>
      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent || 'text-habita-dark'}`}>{value}</p>
    </div>
  );
}

function SourcePill({ label, value, color, testid }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${colors[color] || colors.gray}`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-xl font-bold mt-0.5">{value}</p>
    </div>
  );
}
